/**
 * @module Utils
 *
 */
// The role enum comes from the context module, as in App.tsx: it is the app's
// runtime role vocabulary, and app/models/UserModel must stay a type-only
// import here (see subscription.runtime.test.ts).
import { RoleType } from "app/contexts/UserContext";
import { UserModel, UserPermissionTypes } from "app/models/UserModel";

export function hasCoachPro(user: UserModel | null | undefined): boolean {
  return !!user?.plan?.planCode?.startsWith("coachpro+");
}

export function hasAcademyPlan(user: UserModel | null | undefined): boolean {
  // Academy plan codes are "coachacademy+yearly" / "coachacademy+monthly"
  // (the bare "academy" prefix is kept as a legacy fallback).
  const planCode = user?.plan?.planCode;
  return (
    !!planCode &&
    (planCode.startsWith("coachacademy+") || planCode.startsWith("academy"))
  );
}

/**
 * Whether the Academy onboarding — the setup badge, checklist, wizard, rails,
 * payment guard and first-visit popup — is this user's to see.
 *
 * It belongs to the facility's "Coach - Admin": the coach who bought the
 * Academy plan, whose purchase creates the facility and their membership
 * (role coach with isAdmin) in one step. Facility staff on the same plan —
 * schedulers, admins and plain coaches — share `kind: "coach"` and the plan
 * code, so the plan alone cannot tell them apart. The first membership is
 * judged, as App.tsx does for the role context.
 *
 * Plan-feature gates (Booking Logs, upsell hiding, the Public Landing Page
 * section…) stay on hasAcademyPlan: those are Academy features, not setup.
 */
export function isAcademyOnboardingCoach(
  user: UserModel | null | undefined,
): boolean {
  if (!user || user.kind !== "coach" || !hasAcademyPlan(user)) {
    return false;
  }
  const membership = user.facilityMemberships?.[0];
  return (
    !!membership &&
    membership.role === RoleType.COACH &&
    membership.isAdmin === true
  );
}

/**
 * Membership of a licence pool: a seat paid for by a subscription that is not
 * the holder's own — an organization's pool, or the older kind a colleague's
 * own subscription funds. It says nothing about who may work that subscription;
 * that is plan.canCancel, read through canCancelLicensedPlan.
 */
export function hasLicensedPlan(user: UserModel | null | undefined): boolean {
  return !!user?.plan?.licensed;
}

/**
 * A licensed seat whose holder is no Stripe or Recurly customer of his own:
 * there is no card of his to show or update. That is all it says — the pool's
 * own subscription may still be his to end or resume (canCancelLicensedPlan).
 * The seat is always an organization's, which is why plan.organizationName
 * names it, but who pays may be that organization or a colleague whose own
 * subscription funds the pool, which is plan.payerName.
 */
export function isBilledByOrganization(
  user: UserModel | null | undefined,
): boolean {
  return hasLicensedPlan(user) && !user?.hasBillingInfo;
}

/**
 * A licensed seat whose subscription the holder may end or resume: the
 * organization's own Stripe pool, and he is the coach who bought it or the
 * organization's owner. The API decides (plan.canCancel); ending it ends every
 * seat of the pool at the period end.
 */
export function canCancelLicensedPlan(
  user: UserModel | null | undefined,
): boolean {
  return hasLicensedPlan(user) && !!user?.plan?.canCancel;
}

export function hasAnalyzePlan(user: UserModel): boolean {
  return user.plan?.planCode?.startsWith("analyze");
}

// What the API names as plan.source for a plan bought in a store.
const STORE_SOURCES = ["AppStore", "PlayStore"];

/**
 * A store-bought plan the user has already let go of: cancelled in the App
 * Store or Google Play, which the API reports as "canceled" until the paid
 * period ends. It has no Stripe subscription to re-price, and it does not keep
 * the user from buying a Stripe plan for the time it has left; a plan still
 * renewing in the store does.
 */
export function hasCanceledStorePlan(
  user: UserModel | null | undefined,
): boolean {
  return (
    user?.plan?.status === "canceled" && STORE_SOURCES.includes(user.plan.source)
  );
}

/**
 * The plans the API lets the user's own Stripe subscription move to in place
 * (User::Plans::Base#upgrade_targets), narrowed to the ones a page sells.
 */
export function repriceTargets(
  user: UserModel | null | undefined,
  planCodes: readonly string[],
): string[] {
  const targets = user?.plan?.upgradeTargets ?? [];
  return planCodes.filter((code) => targets.includes(code));
}

export function hasCoachPlus(user: UserModel): boolean {
  return (
    user.subscription.status === "active" ||
    user.subscription.status === "trial"
  );
}

export function hasCoachPlusTrial(user: UserModel): boolean {
  return user.subscription.status === "trial";
}

export function hasExpiredCoachPlus(user: UserModel): boolean {
  return (
    user.subscription.status === "expired" ||
    user.subscription.status === "trial_expired"
  );
}

export function hasExpiredSubscription(user: UserModel): boolean {
  return (
    !!user &&
    !user.hasCoachPlusSubscription &&
    !user.hasTrialSubscription &&
    user.subscription?.status === "expired"
  );
}

export function hasExpiredCoachPlusSubscription(user: UserModel): boolean {
  return user.subscription.status === "expired";
}

export function hasExpiredCoachPlusTrial(user: UserModel): boolean {
  return user.subscription.status === "trial_expired";
}

export function hasTrialSubscription(user: UserModel): boolean {
  return user.hasTrialSubscription;
}

export function hasFreeSlots(user: UserModel): boolean {
  return user.plan.slotsCount < user.plan.spacesLimit;
}

export function permissionFor(
  user: UserModel,
  permission: UserPermissionTypes,
) {
  return user.planPermissions.indexOf(permission) !== -1;
}
